'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { Wallet } from 'lucide-react';
import { formatCurrency } from '@/lib/utils/format';
import { AppImage } from '@/components/common/AppImage';
import { useEffect, useState, useRef } from 'react';

interface CurrentBidProps {
  currentBidAmount: number | null;
  basePrice: number;
  currentBidderTeam: string | null;
  currentBidderLogo?: string | null;
  remainingTime?: number | null;
}

export function CurrentBid({
  currentBidAmount,
  basePrice,
  currentBidderTeam,
  currentBidderLogo = null,
}: CurrentBidProps) {
  const displayedAmount = currentBidAmount ?? basePrice;
  const isBasePrice = currentBidAmount === null || !currentBidderTeam;

  // Track previous amount to detect changes
  const [isNewBid, setIsNewBid] = useState(false);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevAmountRef = useRef<number | null>(null);

  useEffect(() => {
    const prevAmount = prevAmountRef.current;

    if (currentBidAmount !== prevAmount) {
      prevAmountRef.current = currentBidAmount;

      if (prevAmount !== null) {
        setIsNewBid(true);

        if (animationTimeoutRef.current) {
          clearTimeout(animationTimeoutRef.current);
        }
        animationTimeoutRef.current = setTimeout(() => {
          setIsNewBid(false);
          animationTimeoutRef.current = null;
        }, 1500); // Reduced time for better UX
      }
    }

    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }
    };
  }, [currentBidAmount]);

  return (
    <div className="sm:p-1">
      <motion.div
        initial={{ opacity: 0.9 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className={`rounded-xl p-2 text-white shadow-md transition-colors duration-500 sm:p-4 ${
          isBasePrice
            ? 'bg-gradient-to-r from-yellow-600 to-yellow-300'
            : isNewBid
              ? 'bg-gradient-to-r from-primary-500 to-accent-blue shadow-lg ring-2 ring-yellow-300'
              : 'bg-gradient-to-r from-primary-600 to-accent-blue'
        }`}
      >
        {/* 🔹 Header with NEW BID Indicator */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {isBasePrice ? 'Awaiting Bid' : 'Current Bid'}
          </h3>
          <AnimatePresence>
            {isNewBid && !isBasePrice && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                className="rounded-full bg-yellow-300 px-2 py-0.5 text-xs font-bold text-primary-800"
              >
                NEW BID!
              </motion.span>
            )}
          </AnimatePresence>
        </div>

        {/* 🔹 Amount Display with Smoother Animation */}
        <AnimatePresence mode="wait">
          <motion.div
            key={displayedAmount}
            className="mt-2 flex items-center space-x-3 text-2xl font-bold sm:text-3xl"
            initial={{ scale: 0.9, opacity: 0.7 }}
            animate={{
              scale: isNewBid ? [1, 1.1, 1] : 1,
              opacity: 1,
              y: isNewBid ? [0, -5, 0] : 0,
            }}
            transition={{
              duration: 0.4,
              type: 'tween',
              stiffness: 120,
              damping: 10,
            }}
          >
            <Wallet
              className={`h-6 w-6 sm:h-8 sm:w-8 ${
                isNewBid ? 'animate-pulse text-yellow-300' : ''
              }`}
            />
            <span className={isNewBid ? 'text-yellow-300' : ''}>
              {formatCurrency(displayedAmount)}
            </span>
          </motion.div>
        </AnimatePresence>

        {/* 🔹 Last Bidder Info (if applicable) */}
        {currentBidderTeam && !isBasePrice && (
          <motion.div
            className="mt-4 flex items-center space-x-3 text-white"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="relative h-12 w-12 overflow-hidden rounded-full border-2 border-white/70 shadow-lg">
              {/* Subtle glow effect */}
              <div
                className={`absolute -inset-1 rounded-full bg-white/20 blur-sm ${
                  isNewBid ? 'animate-pulse' : ''
                }`}
              ></div>
              <AppImage
                src={currentBidderLogo || ''}
                alt={currentBidderTeam}
                shape="circle"
                size="md"
                gradient
                className="relative h-full w-full object-cover"
                fallbackText={currentBidderTeam}
              />
            </div>
            <span className="text-base font-medium">
              Last bid by{' '}
              <span
                className={`font-bold ${isNewBid ? 'text-yellow-300' : ''}`}
              >
                {currentBidderTeam}
              </span>
            </span>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
